import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import updateCourses from '@/utils/college-listing/updateCourses';
import { X } from 'lucide-react';

import { CourseFilterButtons } from './FilterButtons';
import SearchBar from './SearchBar';

interface CourseSectionProps {
  filterData: any;
  filterOptions: any;
  filterSecStreamData: any;
  setSelectedFilterCategory: (category: string | null) => void;
  handleModalToggle: any;
}

export default function CourseSection({
  filterData,
  filterOptions,
  filterSecStreamData,
  setSelectedFilterCategory,
  handleModalToggle,
}: Readonly<CourseSectionProps>) {
  const [availableCourses, setAvailableCourses] = useState<any[]>([]);

  useEffect(() => {
    if (filterOptions.country) {
      setAvailableCourses(updateCourses(filterData, filterOptions, filterSecStreamData));
    }
  }, [filterData, filterOptions, filterSecStreamData]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCourses, setFilteredCourses] = useState(availableCourses);
  const [noMatchData, setNoMatchData] = useState(false);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      setFilteredCourses(() => {
        const lowerCaseQuery = searchQuery.toLowerCase();

        // First, find states that match the query
        const matchingStates = availableCourses.filter(
          (course: any) =>
            course.course_name.toLowerCase().includes(lowerCaseQuery) ??
            course.course_url.toLowerCase().includes(lowerCaseQuery) ??
            course.abbreviation.toLowerCase().includes(lowerCaseQuery),
        );
        if (matchingStates?.length === 0) {
          setNoMatchData(true);
        } else {
          setNoMatchData(false);
        }

        // Then, find the remaining states that do not match
        const nonMatchingStates = availableCourses.filter(
          (course: any) => !course.course_name.toLowerCase().includes(lowerCaseQuery),
        );
        // Prioritize matching states first
        return [...matchingStates, ...nonMatchingStates];
      });
    } else {
      setFilteredCourses(availableCourses); // Reset if searchQuery is less than 3 chars
    }
  }, [searchQuery, availableCourses]);

  // Redirect state
  const [isRedirecting, setIsRedirecting] = useState(false);

  const router = useRouter();
  const handleUpdateCourse = async (url: string) => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    router.push(`/${url}`);
    setIsRedirecting(false);
    const screenWidth = window.innerWidth;
    if (screenWidth >= 1024) {
      setSelectedFilterCategory(null);
    } else {
      handleModalToggle();
    }
  };

  const locInfo =
    filterOptions.city?.city_url ??
    filterOptions.state?.state_url ??
    filterOptions.country?.country_url?.replace('study-in-', '');

  if (!(Array.isArray(filteredCourses) && filteredCourses?.length > 0)) {
    return null;
  }

  return (
    <>
      {/* Mobile view: */}
      <div className="h-fit w-full lg:hidden">
        <div className="sticky top-0 bg-white">
          {/* Header */}
          <div className="mb-2 text-lg font-bold text-primary">Course</div>

          {/* Search Bar */}
          <SearchBar placeholder="Find Course" searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>

        {searchQuery.length >= 3 && noMatchData && (
          <p className="mt-1 text-center text-sm text-red-500">No Match Found</p>
        )}

        {/* scrollable content */}
        <div
          style={{
            overflow: 'auto',
            msOverflowStyle: 'none', // IE and Edge
            scrollbarWidth: 'none',
          }}
          className="mb-20 mt-4 flex min-h-fit w-full flex-wrap gap-3 overflow-auto sm:grid sm:max-h-full sm:grid-cols-2"
        >
          <CourseFilterButtons
            filteredCourses={filteredCourses}
            filterOptions={filterOptions}
            searchQuery={searchQuery}
            isRedirecting={isRedirecting}
            handleUpdateCourse={handleUpdateCourse}
            locInfo={locInfo}
          />
        </div>
      </div>

      {/* Desktop view: */}
      <div className="absolute left-[24%] !z-10 mt-3 hidden lg:block">
        <div className="h-[25rem] w-[30rem] overflow-y-auto rounded-lg border bg-white shadow-lg">
          {/* Header */}
          <div className="sticky top-0 bg-white px-4 pb-2 pt-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-lg font-medium">Courses</div>
              <button
                onClick={() => setSelectedFilterCategory(null)}
                className="text-2xl text-gray-600 hover:text-gray-900"
              >
                <X className="scale-75" />
              </button>
            </div>

            {/* Search Bar */}
            <SearchBar placeholder="Find Course" searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </div>

          {searchQuery.length >= 3 && noMatchData && (
            <p className="mt-1 text-center text-sm text-red-500">No Match Found</p>
          )}

          {/* Scrollable Content */}
          <div className="flex flex-wrap justify-start gap-x-3 gap-y-4 px-4 pb-4">
            <CourseFilterButtons
              filteredCourses={filteredCourses}
              filterOptions={filterOptions}
              searchQuery={searchQuery}
              isRedirecting={isRedirecting}
              handleUpdateCourse={handleUpdateCourse}
              locInfo={locInfo}
            />
          </div>
        </div>
      </div>
    </>
  );
}
